import { useEffect, useState } from "react";
import { t, BADGE_LABEL, EXPLORE_COPY } from "../copy";
import { isCardGenerating } from "../cardState";
import { resolveLifeArea } from "../lifeAreas";
import LifeAreaTile from "./LifeAreaTile";
import chevronGlass from "../../../Assets/icon/explore/chevron-right-glass.svg";
import arrowBlue from "../../../Assets/icon/explore/arrow-right-blue.svg";

/**
 * The UniversalDialogueCard (guide §6.2), drawn two ways:
 *   variant="feature" — Recommended for You (Figma 9154:8266): artwork behind
 *                       the text, life-area chip and a chevron in the footer
 *   variant="row"     — the Life Area / Self-Knowledge list (Figma
 *                       10968:20520): artwork down the left edge, title, a
 *                       two-line context line and the CTA as a text link
 *   variant="light"   — the older stacked card, kept for any other caller
 *
 * Only `title` is guaranteed. Nothing here is picked by Dialogue type: v26.5
 * removed `dialogue_type` and forbids an icon or label chosen from it, so the
 * life-area tile and chip render only when the card carries
 * `display_area_key` (being added to aris-ai, §7 E1) and are simply absent
 * until then.
 */

/**
 * Optional artwork. `image_url` is a presigned URL that may point at an object
 * the pipeline has not generated yet, so it is shown only once it has actually
 * loaded — the gradient underneath is the fallback, and nothing waits on it.
 * A card whose `generation_state` is GENERATING (gaps E4) skips the load.
 */
function useLoadedImage(src) {
  const [state, setState] = useState("loading");
  useEffect(() => {
    if (!src) return undefined;
    setState("loading");
    let live = true;
    const img = new Image();
    img.onload = () => live && setState("ok");
    img.onerror = () => live && setState("error");
    img.src = src;
    return () => {
      live = false;
    };
  }, [src]);
  return Boolean(src) && state === "ok";
}

/**
 * The card's Life Area. A Recommended card also carries its area's icon
 * (`icon_image_url`, aris-ai 2026-10-22), which wins over the cached Life
 * Areas list so the tile shows even before that list has loaded.
 */
const areaOf = (card) =>
  card?.display_area_key
    ? resolveLifeArea({
        key: card.display_area_key,
        icon_image_url: card.icon_image_url,
      })
    : null;

/**
 * The tile on a Recommended card: its area's, or just the icon for a card
 * with an icon but no area (the Self-Discovery card wears Self-Knowledge's).
 */
const tileOf = (card, area) =>
  area || (card?.icon_image_url ? { iconUrl: card.icon_image_url } : null);

function FeatureCard({ card, onOpen }) {
  const generating = isCardGenerating(card);
  const hasImage = useLoadedImage(generating ? null : card.image_url);
  const area = areaOf(card);
  const tile = tileOf(card, area);

  return (
    <button
      type="button"
      className={`ex-rec${hasImage ? " ex-rec--image" : ""}`}
      style={hasImage ? { backgroundImage: `url("${card.image_url}")` } : undefined}
      disabled={generating}
      aria-busy={generating || undefined}
      onClick={() => onOpen?.(card)}
    >
      <span className="ex-rec__top">
        {tile ? (
          <LifeAreaTile area={tile} className="ex-rec__tile" />
        ) : (
          <span />
        )}
        {card.badge && BADGE_LABEL[card.badge] && (
          <span className="ex-rec__badge">{BADGE_LABEL[card.badge]}</span>
        )}
      </span>

      <span className="ex-rec__body">
        <span className="ex-rec__title">{card.title}</span>
        {card.context_line && (
          <span className="ex-rec__context">{card.context_line}</span>
        )}
      </span>

      <span className="ex-rec__foot">
        {area ? <span className="ex-rec__chip">{area.title}</span> : <span />}
        <span className="ex-rec__chevron">
          <img src={chevronGlass} alt="" width="20" height="20" />
        </span>
      </span>
    </button>
  );
}

function LightCard({ card, onOpen }) {
  const area = areaOf(card);
  const generating = isCardGenerating(card);
  // The artwork takes the tile once it has loaded; until then (or when the
  // object is not generated yet) the life-area glyph stands in.
  const hasImage = useLoadedImage(generating ? null : card.image_url);
  return (
    <button
      type="button"
      className="ex-card ex-card--light"
      disabled={generating}
      aria-busy={generating || undefined}
      onClick={() => onOpen?.(card)}
    >
      {(hasImage || area || card.badge) && (
        <span className="ex-card__top">
          {hasImage ? (
            <span className="ex-card__chip ex-card__chip--art">
              <img src={card.image_url} alt="" />
            </span>
          ) : area ? (
            <LifeAreaTile area={area} className="ex-card__chip" />
          ) : (
            <span />
          )}
          {card.badge && BADGE_LABEL[card.badge] && (
            <span className="ex-card__badge">{BADGE_LABEL[card.badge]}</span>
          )}
        </span>
      )}
      <span className="ex-card__title">{card.title}</span>
      {card.context_line && <span className="ex-card__context">{card.context_line}</span>}
      {generating ? (
        <span className="ex-card__cta ex-card__cta--pending">{EXPLORE_COPY.generating}</span>
      ) : (
        card.cta_key && <span className="ex-card__cta">{t(card.cta_key)}</span>
      )}
    </button>
  );
}

function RowCard({ card, onOpen }) {
  const generating = isCardGenerating(card);
  const hasImage = useLoadedImage(generating ? null : card.image_url);
  const area = areaOf(card);
  // Without artwork (not generated yet, or none) the strip is the area's
  // colour as a gradient, as the design's "Personal Blind Spots" card shows.
  const fallback = area?.color
    ? `linear-gradient(180deg, ${area.color} 0%, #1e1030 100%)`
    : "linear-gradient(180deg, #6c2bb3 0%, #1e1030 100%)";
  return (
    <button
      type="button"
      className="ex-lrow"
      disabled={generating}
      aria-busy={generating || undefined}
      onClick={() => onOpen?.(card)}
    >
      <span
        className="ex-lrow__media"
        style={hasImage ? undefined : { background: fallback }}
      >
        {hasImage && <img src={card.image_url} alt="" />}
      </span>
      <span className="ex-lrow__body">
        <span className="ex-lrow__text">
          <span className="ex-lrow__title">{card.title}</span>
          {card.context_line && (
            <span className="ex-lrow__context">{card.context_line}</span>
          )}
        </span>
        {generating ? (
          <span className="ex-lrow__cta ex-lrow__cta--pending">{EXPLORE_COPY.generating}</span>
        ) : (
          card.cta_key && (
            <span className="ex-lrow__cta">
              {t(card.cta_key)}
              <img src={arrowBlue} alt="" width="16" height="16" />
            </span>
          )
        )}
      </span>
    </button>
  );
}

function DialogueCard({ card, variant = "light", onOpen }) {
  if (!card) return null;
  if (variant === "feature") return <FeatureCard card={card} onOpen={onOpen} />;
  if (variant === "row") return <RowCard card={card} onOpen={onOpen} />;
  return <LightCard card={card} onOpen={onOpen} />;
}

export default DialogueCard;
